import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { Company } from '../../../../../models/company.model';
import { CompanyService } from '../../../../../services/company.service';
import { SILOREAL_APP_URL, SiloBag, SiloBagCommitmentEvent, SiloBagRef } from '../../models/silo-bag.model';
import { SiloBagService } from '../../services/silo-bag.service';
import { SILO_BAG_COVERAGE_MARGIN } from './silo-bag-selector.validators';

@Component({
  selector: 'app-silo-bag-selector',
  templateUrl: './silo-bag-selector.component.html',
  styleUrls: ['./silo-bag-selector.component.scss']
})
export class SiloBagSelectorComponent implements OnInit, OnChanges, OnDestroy {

  /** Tonelaje requerido por la orden ODD. */
  @Input() public orderQuantity: number = 0;
  /** Filtro opcional por tipo de grano (slug del producto). */
  @Input() public grainTypeFilter?: string;
  /** Cuando es true, deshabilita los checkboxes. */
  @Input() public disabled: boolean = false;
  /** Solo lectura: muestra las bolsas ya comprometidas (sin edición). */
  @Input() public readonly: boolean = false;
  /** SiloIDs preseleccionados (modo edición). */
  @Input() public committedIds: string[] = [];
  /**
   * SiloIDs de un borrador aún no persistido (ej. selección hecha en la vista de la orden,
   * recuperada al llegar a la pantalla de Nueva Contraorden). A diferencia de `committedIds`,
   * no implica que el backend ya tenga estas bolsas comprometidas: si alguna dejó de estar
   * disponible mientras tanto, simplemente no aparece preseleccionada.
   */
  @Input() public draftSelectedIds: string[] = [];
  /** Snapshot del backend (`negotiation.silo_bags`) en modo readonly: se ve sin llamar a Siloreal. */
  @Input() public committedSnapshot: SiloBagRef[] = [];
  /** True cuando las bolsas las comprometió la contraparte (ej. productor), no la empresa logueada. */
  @Input() public committedByCounterparty: boolean = false;
  /**
   * True cuando el negocio (Negotiation) que comprometió estas bolsas ya está cerrado
   * (status Booked, id 7). Habilita el link "Asegurar": el snapshot puede existir antes
   * de eso (se persiste en cada envío de contraoferta) sin que el negocio esté cerrado.
   */
  @Input() public isNegotiationClosed: boolean = false;
  /** En edición de contraorden: negociación a excluir del filtrado de comprometidas. */
  @Input() public excludeNegotiationId?: number;
  /**
   * Canje en edición: mantiene visibles las bolsas que el propio productor ya eligió en las
   * negociaciones hermanas del mismo canje (una por corredor, todas por el mismo grano).
   */
  @Input() public excludeBarterId?: number | string;
  /** Cuando está seteado, el listado es el stock del PRODUCTOR de esa negociación, no el de la empresa logueada. */
  @Input() public negotiationId?: number;
  /**
   * SiloIDs base del diff visual ("Nueva"/"Quitada"): lo comprometido hoy, no la propuesta
   * anterior (en el pre-anote no existe). Vacío = sin diff.
   */
  @Input() public baselineIds: string[] = [];

  /** Emite el array de bolsas seleccionadas. */
  @Output() public selectionChange = new EventEmitter<SiloBag[]>();
  /** Emite true cuando la cobertura es válida (suma > requerido * 1.10). */
  @Output() public validityChange = new EventEmitter<boolean>();

  public bags: SiloBag[] = [];
  public selected: { [siloId: string]: boolean } = {};
  public loading: boolean = true;
  public loadError: boolean = false;
  /** True cuando el backend respondió 400: la empresa no tiene credenciales de Siloreal. */
  public missingCredentials: boolean = false;
  public readonly margin = SILO_BAG_COVERAGE_MARGIN;
  public currentPage: number = 1;
  public readonly pageSize: number = 10;

  /** SiloIDs cuya fila de historial está expandida. */
  public expandedHistory: { [siloId: string]: boolean } = {};
  /** Historial ya cargado, por SiloID (cache: no se vuelve a pedir al re-expandir). */
  public history: { [siloId: string]: SiloBagCommitmentEvent[] } = {};
  /** SiloIDs con el historial en vuelo. */
  public historyLoading: { [siloId: string]: boolean } = {};
  /** SiloIDs cuyo historial falló. */
  public historyError: { [siloId: string]: boolean } = {};

  /** Estado de seguro (`insured`) por SiloID, pedido en vivo (ver `showInsuredColumn`). */
  public insuredStatus: Map<string, boolean> = new Map();
  public insuredStatusLoading: boolean = false;
  public insuredStatusError: boolean = false;

  private _searchTerm: string = '';
  public get searchTerm(): string { return this._searchTerm; }
  public set searchTerm(value: string) {
    this._searchTerm = value;
    this.currentPage = 1;
  }
  public readonly silorealAppUrl = SILOREAL_APP_URL;

  private company: Company;
  private destroy$ = new Subject<void>();
  private wasValid: boolean = false;

  constructor(
    private companyService: CompanyService,
    private siloBagService: SiloBagService,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    // En modo readonly las bolsas comprometidas vienen del backend (snapshot persistido):
    // no se llama a Siloreal ni se necesita el listado de la empresa.
    if (this.readonly) {
      this.bags = this.bagsFromSnapshot();
      this.loading = false;
      this.emitValidity();
    }

    // La suscripción corre en ambos modos por simetría y para que `this.company` esté
    // disponible si en el futuro readonly necesita el companyId. Hoy sólo se usa para
    // `loadBags()` y para el historial, ambos exclusivos del modo edición (ver
    // canViewHistory): en readonly no se dispara ninguna llamada.
    this.companyService.watch().pipe(takeUntil(this.destroy$)).subscribe(company => {
      if (!company) return;
      this.company = company;
      if (!this.readonly) this.loadBags();
      if (this.showInsuredColumn && !this.insuredStatusLoading && this.insuredStatus.size === 0) {
        this.loadInsuredStatus();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['committedSnapshot'] && this.readonly) {
      this.bags = this.bagsFromSnapshot();
      this.emitValidity();
    }
    // Los padres bindean getters que rearman el array en cada ciclo: se compara por contenido
    // para no pisar la selección del usuario con la comprometida en cada detección de cambios.
    if (changes['committedIds'] && this.bags.length && this.idsChanged(changes['committedIds'])) {
      this.applyCommittedSelection();
      // loadBags() ya valida después de preseleccionar; acá no hay otro que lo haga.
      this.emitValidity();
    }
    if (changes['draftSelectedIds'] && this.bags.length && this.idsChanged(changes['draftSelectedIds'])) {
      this.applyDraftSelection();
    }
    if ((changes['orderQuantity'] && !changes['orderQuantity'].firstChange) && this.bags.length) {
      // Recalcular la validez cuando cambia la cantidad requerida.
      this.emitValidity();
    }
  }

  /** True cuando el binding cambio de contenido, no solo de referencia. */
  private idsChanged(change: SimpleChange): boolean {
    const previous: string[] = change.previousValue ?? [];
    const current: string[] = change.currentValue ?? [];
    if (change.firstChange) return current.length > 0;
    if (previous.length !== current.length) return true;
    return current.some((id, index) => id !== previous[index]);
  }

  /** Construye los SiloBag mostrados en modo readonly a partir del snapshot del backend. */
  private bagsFromSnapshot(): SiloBag[] {
    return (this.committedSnapshot ?? []).map(ref => {
      const baggedDate = ref.bagged_date ? ref.bagged_date.slice(0, 10) : null;
      return new SiloBag({
        silo_id:            ref.silobag_id,
        silobag_uuid:       ref.silobag_uuid ?? null,
        silobag_name:       ref.silobag_name ?? null,
        grain_type:         (ref.species ?? '').toString().toLowerCase(),
        harvest:            ref.harvest ?? null,
        organization:       ref.organization ?? null,
        input_weight:       this.refNumber(ref.input_weight),
        remaining_stock:    this.refNumber(ref.remaining_stock),
        // El snapshot guarda stock en kg (como Siloreal); la tabla muestra toneladas.
        bagged_weight:      (this.refNumber(ref.remaining_stock) ?? this.refNumber(ref.input_weight) ?? 0) / 1000,
        lot_name:           ref.lot_name ?? null,
        countryside_name:   ref.countryside_name ?? null,
        humidity:           this.refNumber(ref.humidity),
        bagged_date:        baggedDate,
        days_since_bagging: baggedDate ? this.daysSince(baggedDate) : 0,
        latitude:           this.refNumber(ref.latitude),
        longitude:          this.refNumber(ref.longitude),
      });
    });
  }

  private daysSince(dateStr: string): number {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return 0;
    const ms = Date.now() - new Date(year, month - 1, day).getTime();
    return Math.floor(ms / 86_400_000);
  }

  private refNumber(raw: number | string | null | undefined): number | null {
    if (raw == null || raw === '') return null;
    const parsed = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadBags(): void {
    this.loading = true;
    this.loadError = false;
    this.missingCredentials = false;

    // Modo "stock ajeno": pide el listado contra el PRODUCTOR, no contra this.company.
    const source$ = this.negotiationId != null
      ? this.siloBagService.listForNegotiation(this.company.id, this.negotiationId)
      : this.siloBagService.listByCompany(this.company.id, this.excludeNegotiationId, this.excludeBarterId);

    source$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: bags => {
          this.bags = bags;
          this.applyCommittedSelection();
          this.applyDraftSelection();
          this.loading = false;
          this.emitValidity();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          // 400 = la empresa no tiene credenciales de Siloreal cargadas: hay que
          // indicarle al usuario que las configure, no es un error de conexión.
          this.missingCredentials = err?.status === 400;
          this.loadError = !this.missingCredentials;
          this.validityChange.emit(false);
        }
      });
  }

  /** Ruta a la pantalla de integraciones donde se cargan las credenciales de Siloreal. */
  public get integrationsLink(): string {
    return this.company ? `/company/${this.company.id}/integrations` : '';
  }

  /** Bolsas que se muestran en la tabla según el modo. */
  public get displayBags(): SiloBag[] {
    if (this.readonly) {
      return this.committedBags;
    }
    return this.availableBags;
  }

  /** Bolsas filtradas por el término de búsqueda (nombre y lote). */
  public get filteredDisplayBags(): SiloBag[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.displayBags;
    return this.displayBags.filter(bag =>
      (bag.silobag_name ?? '').toLowerCase().includes(term) ||
      (bag.lot_name ?? '').toLowerCase().includes(term) ||
      (bag.silo_id ?? '').toLowerCase().includes(term)
    );
  }

  public get totalPages(): number {
    return Math.ceil(this.filteredDisplayBags.length / this.pageSize);
  }

  public get paginatedBags(): SiloBag[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredDisplayBags.slice(start, start + this.pageSize);
  }

  public get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const delta = 2;
    const left = Math.max(2, this.currentPage - delta);
    const right = Math.min(total - 1, this.currentPage + delta);
    const pages: number[] = [1];
    if (left > 2) pages.push(-1);
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push(-1);
    pages.push(total);
    return pages;
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  /** Comprometidas de esta negociación: en readonly `this.bags` ya es el snapshot; en edición filtra por `committedIds`. */
  public get committedBags(): SiloBag[] {
    if (this.readonly) return this.bags;
    if (!this.committedIds?.length) return [];
    const ids = new Set(this.committedIds);
    return this.bags.filter(bag => ids.has(bag.silo_id));
  }

  /** Bolsas disponibles para asignar (no comprometidas con otra orden, y compatibles con el grano). */
  public get availableBags(): SiloBag[] {
    return this.bags.filter(bag => {
      if (bag.assigned_order_id != null) return false;
      if (this.grainTypeFilter && bag.grain_type !== this.grainTypeFilter) return false;
      return true;
    });
  }

  public get selectedBags(): SiloBag[] {
    if (this.readonly) {
      return this.committedBags;
    }
    return this.availableBags.filter(bag => this.selected[bag.silo_id]);
  }

  public get selectedTotal(): number {
    return this.selectedBags.reduce((sum, b) => sum + (Number(b.bagged_weight) || 0), 0);
  }

  public get requiredCoverage(): number {
    return (Number(this.orderQuantity) || 0) * this.margin;
  }

  public get hasValidCoverage(): boolean {
    return this.selectedTotal > this.requiredCoverage && this.selectedBags.length > 0;
  }

  /** Sólo hay diff si hay algo comprometido: sin baseline, toda bolsa tildada sería "Nueva" y no aporta. */
  public get showsDiff(): boolean {
    return !this.readonly && (this.baselineIds?.length ?? 0) > 0;
  }

  /** Bolsa tildada que NO estaba en el turno anterior. */
  public isNewlyProposed(bag: SiloBag): boolean {
    return this.showsDiff && !!this.selected[bag.silo_id] && !this.baselineIds.includes(bag.silo_id);
  }

  /** Bolsa del turno anterior que ya NO está tildada. */
  public isRemovedFromBaseline(bag: SiloBag): boolean {
    return this.showsDiff && !this.selected[bag.silo_id] && this.baselineIds.includes(bag.silo_id);
  }

  /** Cuántas bolsas se agregaron/quitaron respecto del baseline. `null` = sin cambios (o sin diff). */
  public get siloBagChanges(): { added: number; removed: number } | null {
    if (!this.showsDiff) return null;

    const baseline = new Set(this.baselineIds);
    const current = new Set(this.selectedBags.map(bag => bag.silo_id));

    let added = 0;
    current.forEach(id => { if (!baseline.has(id)) added++; });
    let removed = 0;
    baseline.forEach(id => { if (!current.has(id)) removed++; });

    return (added > 0 || removed > 0) ? { added, removed } : null;
  }

  public get committedHelpKey(): string {
    return this.committedByCounterparty
      ? 'SILO_BAGS.COMMITTED_HELP_COUNTERPARTY'
      : 'SILO_BAGS.COMMITTED_HELP';
  }

  private applyCommittedSelection(): void {
    this.selected = {};
    (this.committedIds ?? []).forEach(id => {
      this.selected[id] = true;
    });

    // Se emite siempre (incluso vacío) para que el padre no envíe una selección vieja
    // ni pierda la preselección si se envía sin tocar el selector.
    this.selectionChange.emit(this.selectedBags);
  }

  /**
   * Preselecciona un borrador (`draftSelectedIds`) sólo contra bolsas que siguen disponibles
   * (`availableBags`). Si una bolsa del borrador fue comprometida por otra negociación mientras
   * tanto, se ignora en vez de marcarla seleccionada-pero-invisible: `selectedTotal` y la tabla
   * quedarían inconsistentes entre sí si se marcara igual.
   */
  private applyDraftSelection(): void {
    if (!this.draftSelectedIds?.length || this.committedIds?.length) return;
    const availableIds = new Set(this.availableBags.map(bag => bag.silo_id));
    const restoredIds = this.draftSelectedIds.filter(id => availableIds.has(id));
    restoredIds.forEach(id => { this.selected[id] = true; });

    // Feedback explícito: sin esto, los checkboxes aparecen tildados sin que el usuario
    // entienda si fue él o el sistema, y si algo quedó afuera no hay forma de distinguirlo
    // del simple "todavía no seleccionaste suficiente" (mismo mensaje COVERAGE_KO).
    if (restoredIds.length) {
      this.toastrService.info(this.translateService.instant('SILO_BAGS.DRAFT_RESTORED'));
    }
    const unavailableCount = this.draftSelectedIds.length - restoredIds.length;
    if (unavailableCount > 0) {
      this.toastrService.warning(this.translateService.instant('SILO_BAGS.DRAFT_BAGS_UNAVAILABLE', { count: unavailableCount }));
    }

    // Restaurar un borrador ya válido no es un cambio que el usuario acaba de hacer: no
    // corresponde el toast de "cobertura suficiente" que emitValidity() dispara en esa
    // transición (ver `wasValid`).
    this.wasValid = this.hasValidCoverage;
    this.selectionChange.emit(this.selectedBags);
    this.emitValidity();
  }

  public toggle(bag: SiloBag): void {
    if (this.disabled || this.readonly) return;
    this.selected[bag.silo_id] = !this.selected[bag.silo_id];
    this.selectionChange.emit(this.selectedBags);
    this.emitValidity();
  }

  public baggedDateValue(bag: SiloBag): Date | null {
    if (!bag.bagged_date) return null;
    const [year, month, day] = bag.bagged_date.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  public hasCoordinates(bag: SiloBag): boolean {
    return bag.latitude != null && bag.longitude != null;
  }

  public openInGoogleMaps(bag: SiloBag, event: Event): void {
    event.stopPropagation();
    if (!this.hasCoordinates(bag)) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${bag.latitude},${bag.longitude}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** True si la fila de historial de esa bolsa está abierta. */
  /**
   * ¿Se ofrece la trazabilidad de la bolsa?
   *
   * Sólo en modo edición, es decir sólo al dueño del grano. La bitácora
   * (`silobag_commitment_events`) está particionada por `company_id`: son los negocios de
   * la empresa dueña de la silobolsa. En modo readonly quien mira es la contraparte
   * (proveedor de insumos, distribuidor o corredor en un canje), que con su propia
   * `company_id` recibiría siempre una lista vacía — un botón que no explica nada. Y
   * dárselo poblado sería peor: expondría con qué otros negocios el productor comprometió
   * su grano.
   */
  public get canViewHistory(): boolean {
    // Mismo motivo: en modo "stock ajeno" la company_id sigue siendo la del comprador, no la del productor.
    return !this.readonly && this.negotiationId == null;
  }

  /**
   * Clave i18n del título. En modo "stock ajeno" las bolsas no son propias del comprador,
   * así que "a comprometer" (vereda del dueño del grano) lo confundiría.
   */
  public get titleKey(): string {
    if (this.readonly) return 'SILO_BAGS.COMMITTED_TITLE';
    return this.negotiationId != null ? 'SILO_BAGS.TITLE_COUNTERPARTY' : 'SILO_BAGS.TITLE';
  }

  /** Clave i18n de la ayuda: mismo criterio que `titleKey`. */
  public get helpKey(): string {
    return this.negotiationId != null ? 'SILO_BAGS.HELP_COUNTERPARTY' : 'SILO_BAGS.HELP';
  }

  /** Clave i18n del aviso sin stock: en modo "stock ajeno" no es "cargá tus credenciales" (son las del productor). */
  public get missingStockMessageKey(): string {
    return this.negotiationId != null
      ? 'SILO_BAGS.COUNTERPARTY_NO_STOCK'
      : 'SILO_BAGS.MISSING_CREDENTIALS';
  }

  /** True cuando corresponde ofrecer el link a "Cargar credenciales" (sólo cuenta propia). */
  public get showIntegrationsLink(): boolean {
    return this.negotiationId == null;
  }

  /**
   * ¿Se muestra la columna "Asegurado"? Mismo gate que el botón "Asegurar": recién tiene
   * sentido cuando el negocio que comprometió las bolsas ya está cerrado.
   */
  public get showInsuredColumn(): boolean {
    return this.readonly && this.isNegotiationClosed;
  }

  public isInsured(bag: SiloBag): boolean {
    return this.insuredStatus.get(bag.silo_id) === true;
  }

  /** Pide a Siloreal el estado de seguro de todas las bolsas de la empresa (ver `SiloBagService.listInsuredStatus`). */
  private loadInsuredStatus(): void {
    if (!this.company) return;
    this.insuredStatusLoading = true;
    this.insuredStatusError = false;
    this.siloBagService.listInsuredStatus(this.company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => {
          this.insuredStatus = status;
          this.insuredStatusLoading = false;
        },
        error: () => {
          this.insuredStatusLoading = false;
          this.insuredStatusError = true;
        }
      });
  }

  public isHistoryExpanded(bag: SiloBag): boolean {
    return !!this.expandedHistory[bag.silo_id];
  }

  /**
   * Abre/cierra la fila de historial. La carga es lazy: recién al abrir por primera vez
   * se pega al endpoint, así abrir el listado no dispara N requests.
   */
  public toggleHistory(bag: SiloBag, event?: Event): void {
    if (event) event.stopPropagation();

    const id = bag.silo_id;
    const willExpand = !this.expandedHistory[id];
    this.expandedHistory[id] = willExpand;

    if (!willExpand) return;
    // Ya cargado (o cargando): no repetir el pedido.
    if (this.history[id] || this.historyLoading[id]) return;

    this.loadHistory(bag);
  }

  /** Reintento manual tras un error de carga del historial. */
  public retryHistory(bag: SiloBag, event?: Event): void {
    if (event) event.stopPropagation();
    this.loadHistory(bag);
  }

  private loadHistory(bag: SiloBag): void {
    const id = bag.silo_id;
    if (!this.company || !id) {
      this.historyError[id] = true;
      return;
    }

    this.historyLoading[id] = true;
    this.historyError[id] = false;

    this.siloBagService.getHistory(this.company.id, id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: events => {
          this.history[id] = events;
          this.historyLoading[id] = false;
        },
        error: () => {
          this.historyLoading[id] = false;
          this.historyError[id] = true;
        }
      });
  }

  /** Eventos ya cargados de una bolsa (array vacío si todavía no llegaron). */
  public historyEvents(bag: SiloBag): SiloBagCommitmentEvent[] {
    return this.history[bag.silo_id] ?? [];
  }

  public isHistoryLoading(bag: SiloBag): boolean {
    return !!this.historyLoading[bag.silo_id];
  }

  public hasHistoryError(bag: SiloBag): boolean {
    return !!this.historyError[bag.silo_id];
  }

  /** True cuando ya respondió el backend y no hay movimientos registrados. */
  public isHistoryEmpty(bag: SiloBag): boolean {
    const id = bag.silo_id;
    return !this.historyLoading[id] && !this.historyError[id]
      && Array.isArray(this.history[id]) && this.history[id].length === 0;
  }

  /** Cantidad de columnas de la tabla (para el colspan de la fila expandida). */
  public get historyColspan(): number {
    // SiloID, nombre, lote, campo, grano, peso, humedad, fecha, días, ubicación = 10 fijas,
    // + la del checkbox (sólo en edición) + la del toggle de historial (sólo si se ofrece).
    // La fila expandible únicamente existe cuando canViewHistory es true, así que en la
    // práctica esto se evalúa siempre en modo edición; se deja general por si eso cambia.
    return 10 + (this.readonly ? 0 : 1) + (this.canViewHistory ? 1 : 0);
  }

  /** Fecha del evento como Date (null si el backend mandó algo no parseable). */
  public eventDate(event: SiloBagCommitmentEvent): Date | null {
    if (!event?.date) return null;
    const parsed = new Date(event.date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Texto de la acción. Se traduce por clave; si el backend suma una acción nueva que el
   * front no conoce, se muestra el valor crudo en vez de una clave sin traducir.
   */
  public actionLabel(event: SiloBagCommitmentEvent): string {
    return this.translateOrRaw('SILO_BAGS.HISTORY_ACTION.', event?.action);
  }

  /** Texto del motivo (taxonomía del backend). Vacío si el evento no trae reason. */
  public reasonLabel(event: SiloBagCommitmentEvent): string {
    if (!event?.reason) return '';
    return this.translateOrRaw('SILO_BAGS.HISTORY_REASON.', event.reason);
  }

  /** Actor del evento: el usuario, o "Sistema" cuando no hubo actor humano. */
  public actorLabel(event: SiloBagCommitmentEvent): string {
    return event?.user || this.translateService.instant('SILO_BAGS.HISTORY_SYSTEM');
  }

  /** Referencia legible al negocio que originó el evento (orden o negociación). */
  public referenceLabel(event: SiloBagCommitmentEvent): string {
    return event?.order_reference || event?.negotiation_reference || '';
  }

  /** Link a la orden que originó el evento, si el backend mandó su id. */
  public orderLink(event: SiloBagCommitmentEvent): string | null {
    if (!this.company || !event?.order_id) return null;
    return `/company/${this.company.id}/order/${event.order_id}`;
  }

  /**
   * Traduce `prefix + KEY`; si no existe la traducción (ngx-translate devuelve la clave
   * tal cual), cae al valor crudo del backend para no mostrar una clave técnica.
   */
  private translateOrRaw(prefix: string, value: string | null | undefined): string {
    if (!value) return '';
    const key = prefix + value.toUpperCase();
    const translated = this.translateService.instant(key);
    return translated === key ? value : translated;
  }

  private emitValidity(): void {
    if (this.readonly) {
      this.validityChange.emit(this.committedBags.length > 0);
      return;
    }
    const valid = this.hasValidCoverage;
    if (valid && !this.wasValid) {
      this.toastrService.success(this.translateService.instant('SILO_BAGS.COVERAGE_OK'));
    }
    this.wasValid = valid;
    this.validityChange.emit(valid);
  }
}
